import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

// Custom hook for typewriter effect
const useTypewriter = (text: string, startDelay: number = 0, speed: number = 50) => {
  const [displayText, setDisplayText] = useState('');
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    const startTyping = () => {
      let currentIndex = 0;
      const typeNextChar = () => {
        if (currentIndex <= text.length) {
          setDisplayText(text.slice(0, currentIndex));
          currentIndex++;
          timeout = setTimeout(typeNextChar, speed);
        } else {
          setIsComplete(true);
        }
      };
      timeout = setTimeout(typeNextChar, startDelay);
    };
    startTyping();
    return () => clearTimeout(timeout);
  }, [text, startDelay, speed]);

  return { displayText, isComplete };
};

// Enhanced AnimatedLines component
const AnimatedLines = () => {
  const lines = Array(12).fill(null);  // Increased number of lines for more visual density
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {lines.map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-[1px] h-[600px] bg-gradient-to-b from-purple-500/10 via-pink-500/10 to-transparent"
          style={{
            left: `${(i + 1) * 8}%`,  // More evenly distributed
            filter: 'blur(0.8px)',     // Slightly increased blur
          }}
          animate={{
            y: [-600, 1400],
            opacity: [0, 0.4, 0],
          }}
          transition={{
            duration: 20 + i * 2,      // Slower, more varied animation
            repeat: Infinity,
            ease: "linear",
            delay: i * 0.6,
          }}
        />
      ))}
    </div>
  );
};

// Add a new animated background pattern component
const AnimatedBackground = () => (
  <div className="absolute inset-0 opacity-10">
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(192,132,252,0.08),rgba(0,0,0,0))]" />
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(244,114,182,0.08),rgba(0,0,0,0))]" />
    <motion.div
      className="absolute inset-0"
      animate={{
        background: [
          'radial-gradient(circle at 50% 50%, rgba(192,132,252,0.04) 0%, transparent 70%)',
          'radial-gradient(circle at 50% 50%, rgba(244,114,182,0.04) 0%, transparent 70%)',
          'radial-gradient(circle at 50% 50%, rgba(192,132,252,0.04) 0%, transparent 70%)',
        ],
      }}
      transition={{
        duration: 8,
        repeat: Infinity,
        ease: "linear"
      }}
    />
  </div>
);

interface CountUpProps {
  end: number;
  duration?: number;
}

// Animated counter component
const CountUp: React.FC<CountUpProps> = ({ end, duration = 2 }) => {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    let startTimestamp: number | null = null;
    const animate = (timestamp: number) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / (duration * 1000), 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }, [end, duration]);

  return <span>{count}</span>;
};

// Custom cursor
const CustomCursor = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const updateMousePosition = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', updateMousePosition);
    return () => window.removeEventListener('mousemove', updateMousePosition);
  }, []);

  return (
    <motion.div
      className="fixed w-4 h-4 bg-gradient-to-r from-purple-400/60 to-pink-400/60 rounded-full pointer-events-none mix-blend-screen z-50"
      animate={{ x: mousePosition.x - 8, y: mousePosition.y - 8 }}
      transition={{ type: "spring", stiffness: 500, damping: 28 }}
    />
  );
};

// Add the interface for problem-solution pairs
interface ProblemSolution {
  problem: string;
  solution: string;
}

const problemSolutions: ProblemSolution[] = [
  {
    problem: "Struggle with interview nerves?",
    solution: "Our AI helps you practice in a pressure-free setting."
  },
  {
    problem: "Not sure what interviewers want?",
    solution: "Get real-time AI feedback on your answers."
  },
  {
    problem: "Worried about tough questions?",
    solution: "Our AI predicts and prepares you for them."
  },
  {
    problem: "Don't have a mentor to guide you?",
    solution: "Get structured feedback like from a human coach."
  },
  {
    problem: "Feeling unprepared for resume-specific questions?",
    solution: "AI tailors questions to your resume fields."
  },
  {
    problem: "Not confident in your articulation skills?",
    solution: "Practice with AI to refine your answers for more clarity."
  },
  {
    problem: "Lack time for mock interviews?",
    solution: "AI-driven practice sessions are available 24/7."
  },
  {
    problem: "Need to improve your answers quickly?",
    solution: "AI scoring and feedback help you refine responses in minutes."
  },
  {
    problem: "Want to stand out from other candidates?",
    solution: "AI helps guide you to what makes for strong, compelling answers."
  },
  {
    problem: "Struggling with the unpredictability of interviews?",
    solution: "Our comprehensive question generation ensures you are prepared for every angle."
  }
];

const LandingPage: React.FC = () => {
  const { scrollYProgress } = useScroll();
  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  const { user } = useAuth();
  const navigate = useNavigate();

  // Add typewriter effects for section headings and title
  const unlockYour = useTypewriter("Unlock Your ", 500, 70);
  const interviewSuperpowers = useTypewriter("MBA Interview Preparation ", 1500, 70);
  const withAI = useTypewriter("with AI", 3000, 70);
  const interviewModelsTitle = useTypewriter("Interview Models", 4000, 70);
  const capabilitiesTitle = useTypewriter("Capabilities", 4500, 70);
  const faqTitle = useTypewriter("FAQ", 5000, 70);
  const ctaTitle = useTypewriter("Ready to Transform Your Interview Skills?", 5500, 70);

  // Add state for FAQ items
  const [openFaqIndices, setOpenFaqIndices] = useState<number[]>([]);

  // Simplified transforms - only Y translations, no opacity changes
  const heroY = useTransform(smoothProgress, [0, 0.2], [0, -50]);
  const capabilitiesY = useTransform(smoothProgress, [0.15, 0.25], [50, 0]);
  const frameworkY = useTransform(smoothProgress, [0.3, 0.4], [50, 0]);
  const modelsY = useTransform(smoothProgress, [0.45, 0.55], [50, 0]);
  const problemsY = useTransform(smoothProgress, [0.6, 0.7], [50, 0]);
  const faqY = useTransform(smoothProgress, [0.75, 0.85], [50, 0]);
  const ctaY = useTransform(smoothProgress, [0.85, 0.95], [50, 0]);

  const handleGetStarted = () => {
    if (user) {
      navigate('/interview');
    } else {
      navigate('/signin');
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0F17] py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Hero Section with motion values */}
        <motion.section
          style={{ y: heroY }}
          className="min-h-[95vh] flex items-center justify-center text-center py-12 sm:py-16 lg:py-[80px] relative"
        >
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="max-w-7xl relative z-10"
          >
            {/* Side by side container */}
            <div className="flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-12 mb-12">
              {/* Jazz Band Illustration */}
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8 }}
                className="relative w-full lg:w-1/2 max-w-xl"
              >
                <motion.div
                  className="absolute -inset-0.5 bg-gradient-to-r from-purple-500/30 via-pink-500/30 to-purple-400/30 rounded-2xl blur-2xl opacity-75"
                  animate={{
                    opacity: [0.5, 0.8, 0.5],
                    scale: [0.98, 1.01, 0.98]
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                />
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src="/jazz-band-illustration.png" 
                    alt="Jazz band illustration showing diverse musicians collaborating"
                    className="w-full h-auto rounded-2xl transform hover:scale-102 transition-transform duration-500"
                  />
                </div>
              </motion.div>

              {/* Main Heading */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="w-full lg:w-1/2 text-left lg:text-left"
              >
                <motion.h1 
                  className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-inter font-extrabold mb-4 sm:mb-6 leading-[1.1] tracking-tight relative"
                >
                  <motion.span
                    className="inline-block bg-gradient-to-r from-purple-300 via-pink-300 to-purple-200 bg-clip-text text-transparent"
                    animate={{
                      backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
                    }}
                    transition={{
                      duration: 8,
                      repeat: Infinity,
                      ease: "linear"
                    }}
                    style={{ backgroundSize: "200% 200%" }}
                  >
                    {unlockYour.displayText}{" "}
                  </motion.span>
                  <motion.span
                    className="inline-block bg-gradient-to-r from-pink-300 via-purple-300 to-pink-200 bg-clip-text text-transparent relative"
                    animate={{
                      backgroundPosition: ["100% 50%", "0% 50%", "100% 50%"],
                      textShadow: unlockYour.isComplete ? [
                        "0 0 20px rgba(236, 72, 153, 0)",
                        "0 0 50px rgba(236, 72, 153, 0.3)",
                        "0 0 20px rgba(236, 72, 153, 0)",
                      ] : "none",
                    }}
                    transition={{
                      duration: 8,
                      repeat: Infinity,
                      ease: "linear"
                    }}
                    style={{ backgroundSize: "200% 200%" }}
                  >
                    {interviewSuperpowers.displayText}{" "}
                  </motion.span>
                  <motion.span
                    className="inline-block bg-gradient-to-r from-purple-300 to-pink-300 bg-clip-text text-transparent"
                    animate={{
                      scale: interviewSuperpowers.isComplete ? [1, 1.1, 1] : 1,
                      textShadow: interviewSuperpowers.isComplete ? [
                        "0 0 20px rgba(168, 85, 247, 0)",
                        "0 0 40px rgba(168, 85, 247, 0.2)",
                        "0 0 20px rgba(168, 85, 247, 0)",
                      ] : "none",
                    }}
                    transition={{
                      duration: 4,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  >
                    {withAI.displayText}
                  </motion.span>
                </motion.h1>
              </motion.div>
            </div>

            {/* Centered content below */}
            <div className="text-center">
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="text-xl sm:text-2xl text-gray-400 max-w-3xl mx-auto mb-8"
              >
                Your AI Interview Coach for MBA Admissions
              </motion.p>
              <div className="flex flex-col sm:flex-row justify-center gap-8 sm:gap-16 mb-8 sm:mb-16">
                <motion.div 
                  className="text-center relative group"
                  whileHover={{ scale: 1.05 }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                >
                  <div className="absolute -inset-4 bg-gradient-to-r from-purple-500/10 to-transparent rounded-lg blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="relative">
                    <div className="text-3xl sm:text-4xl lg:text-5xl font-bold text-purple-400 mb-2 sm:mb-3">
                      {/* <CountUp end={1} />st */}
                      1st
                    </div>
                    <div className="text-sm text-gray-400 font-medium uppercase tracking-wide">AI-powered MBA Interview Platform In India</div>
                  </div>
                </motion.div>
                <motion.div 
                  className="text-center relative group"
                  whileHover={{ scale: 1.05 }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                >
                  <div className="absolute -inset-4 bg-gradient-to-r from-pink-500/10 to-transparent rounded-lg blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="relative">
                    <div className="text-3xl sm:text-4xl lg:text-5xl font-bold text-pink-400 mb-2 sm:mb-3">
                      <CountUp end={700} duration={3} />+
                    </div>
                    <div className="text-sm text-gray-400 font-medium uppercase tracking-wide">IIM Interviews conducted as a panelist</div>
                  </div>
                </motion.div>
              </div>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="group relative px-6 sm:px-12 py-4 sm:py-6 bg-gradient-to-r from-purple-500 via-pink-500 to-purple-400 rounded-2xl text-lg sm:text-xl font-[Cabinet Grotesk] font-semibold transition-all duration-300 w-full sm:w-auto"
                onClick={handleGetStarted}
              >
                <span className="absolute inset-0 bg-white mix-blend-overlay opacity-0 group-hover:opacity-10 transition-opacity duration-300 rounded-2xl" />
                <motion.span className="relative z-10 text-white">
                  Get Started
                </motion.span>
              </motion.button>
            </div>
          </motion.div>
        </motion.section>

        {/* Capabilities Section */}
        <motion.section
          style={{ y: capabilitiesY }}
          className="py-16 px-4 relative w-full"
        >
          <div className="absolute inset-0 bg-gradient-to-b from-purple-500/5 via-pink-500/5 to-purple-400/5 backdrop-blur-3xl" />
          
          <div className="relative z-10 w-full max-w-6xl mx-auto">
            <motion.h2 
              className="text-3xl md:text-4xl text-center font-inter font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent mb-16"
              animate={{
                textShadow: capabilitiesTitle.isComplete ? [
                  "0 0 20px rgba(168, 85, 247, 0)",
                  "0 0 40px rgba(168, 85, 247, 0.2)",
                  "0 0 20px rgba(168, 85, 247, 0)",
                ] : "none",
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              {capabilitiesTitle.displayText}
            </motion.h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-12">
              <motion.div 
                whileHover={{ y: -5 }}
                className="bg-[#1a1a2e]/30 backdrop-blur-sm border border-gray-800/50 p-6 sm:p-8 lg:p-10 rounded-2xl hover:border-purple-500/30 transition-all duration-300"
              >
                <div className="text-3xl sm:text-4xl mb-6 sm:mb-8">
                  <svg className="w-12 h-12 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <h3 className="text-lg sm:text-xl font-inter font-semibold bg-gradient-to-r from-purple-300 to-purple-200 bg-clip-text text-transparent mb-3 sm:mb-4">Personalized Practice</h3>
                <p className="text-gray-400 leading-relaxed">
                  Tailored interviews based on <i>your</i> resume - your story.
                </p>
              </motion.div>
              <motion.div 
                whileHover={{ y: -5 }}
                className="bg-[#1a1a2e]/30 backdrop-blur-sm border border-gray-800/50 p-6 sm:p-8 lg:p-10 rounded-2xl hover:border-pink-500/30 transition-all duration-300"
              >
                <div className="text-3xl sm:text-4xl mb-6 sm:mb-8">
                  <svg className="w-12 h-12 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-lg sm:text-xl font-inter font-semibold bg-gradient-to-r from-pink-300 to-purple-200 bg-clip-text text-transparent mb-3 sm:mb-4">Calibrated Feedback</h3>
                <p className="text-gray-400 leading-relaxed">
                  Quantitative Feedback that reflects how real interviewers evaluate candidates - what truly matters
                </p>
              </motion.div>
              <motion.div 
                whileHover={{ y: -5 }}
                className="bg-[#1a1a2e]/30 backdrop-blur-sm border border-gray-800/50 p-6 sm:p-8 lg:p-10 rounded-2xl hover:border-pink-500/30 transition-all duration-300"
              >
                <div className="text-3xl sm:text-4xl mb-6 sm:mb-8">
                  <svg className="w-12 h-12 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                  </svg>
                </div>
                <h3 className="text-lg sm:text-xl font-inter font-semibold bg-gradient-to-r from-pink-300 to-purple-200 bg-clip-text text-transparent mb-3 sm:mb-4">Realistic Simulations</h3>
                <p className="text-gray-400 leading-relaxed">
                  Engineered to mimic real-life interview experiences
                </p>
              </motion.div>
              <motion.div 
                whileHover={{ y: -5 }}
                className="bg-[#1a1a2e]/30 backdrop-blur-sm border border-gray-800/50 p-6 sm:p-8 lg:p-10 rounded-2xl hover:border-pink-500/30 transition-all duration-300"
              >
                <div className="text-3xl sm:text-4xl mb-6 sm:mb-8">
                  <svg className="w-12 h-12 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
                  </svg>
                </div>
                <h3 className="text-lg sm:text-xl font-inter font-semibold bg-gradient-to-r from-pink-300 to-purple-200 bg-clip-text text-transparent mb-3 sm:mb-4">Diverse. Comprehensive.</h3>
                <p className="text-gray-400 leading-relaxed">
                  Our engineering ensures comprehensive coverage of <i>your</i> profile that maximizes preparation
                </p>
              </motion.div>
              <motion.div 
                whileHover={{ y: -5 }}
                className="bg-[#1a1a2e]/30 backdrop-blur-sm border border-gray-800/50 p-6 sm:p-8 lg:p-10 rounded-2xl hover:border-pink-500/30 transition-all duration-300"
              >
                <div className="text-3xl sm:text-4xl mb-6 sm:mb-8">
                  <svg className="w-12 h-12 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                  </svg>
                </div>
                <h3 className="text-lg sm:text-xl font-inter font-semibold bg-gradient-to-r from-pink-300 to-purple-200 bg-clip-text text-transparent mb-3 sm:mb-4">Deeper Probing</h3>
                <p className="text-gray-400 leading-relaxed">
                  Our tech digs deeper, beyond the surface-level answers helping you think through your responses
                </p>
              </motion.div>
              <motion.div 
                whileHover={{ y: -5 }}
                className="bg-[#1a1a2e]/30 backdrop-blur-sm border border-gray-800/50 p-6 sm:p-8 lg:p-10 rounded-2xl hover:border-pink-500/30 transition-all duration-300"
              >
                <div className="text-3xl sm:text-4xl mb-6 sm:mb-8">
                  <svg className="w-12 h-12 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-lg sm:text-xl font-inter font-semibold bg-gradient-to-r from-pink-300 to-purple-200 bg-clip-text text-transparent mb-3 sm:mb-4">State-of-the-art Tech</h3>
                <p className="text-gray-400 leading-relaxed">
                  We leverage top of the line LLMs. No shortcuts. Period.
                </p>
              </motion.div>
          
            </div>
          </div>
        </motion.section>

        {/* Cognitive Framework Section */}
        <motion.section
          style={{ y: frameworkY }}
          className="py-16 px-4 relative w-full mb-24"
        >
          <div className="absolute inset-0 bg-gradient-to-b from-purple-500/5 via-pink-500/5 to-purple-400/5 backdrop-blur-3xl" />
          
          <div className="relative z-10 w-full max-w-6xl mx-auto">
            <motion.h2 
              className="text-3xl md:text-4xl text-center font-inter font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent mb-12"
              animate={{
                textShadow: [
                  "0 0 20px rgba(168, 85, 247, 0)",
                  "0 0 40px rgba(168, 85, 247, 0.2)",
                  "0 0 20px rgba(168, 85, 247, 0)",
                ]
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              Cognitive Framework
            </motion.h2>

            {/* Two-column layout container */}
            <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
              {/* Left column - Circuit Diagram */}
              <div className="w-full lg:w-1/2 relative">
                {/* Circuit paths */}
                <svg className="w-full h-[400px]" viewBox="0 0 1000 600" preserveAspectRatio="xMidYMid meet">
                  {/* Main horizontal paths with enhanced visibility and animation */}
                  <motion.path
                    d="M100,50 L400,50 L400,150 L700,150 L700,50 L900,50"
                    className="stroke-gray-600/80 stroke-[2.5px]"
                    fill="none"
                    initial={{ pathLength: 0, opacity: 0 }}
                    animate={{ pathLength: 1, opacity: 1 }}
                    transition={{ duration: 2, ease: "easeInOut" }}
                  />
                  <motion.path
                    d="M100,300 L300,300 L300,350 L600,350 L600,300 L900,300"
                    className="stroke-gray-600/80 stroke-[2.5px]"
                    fill="none"
                    initial={{ pathLength: 0, opacity: 0 }}
                    animate={{ pathLength: 1, opacity: 1 }}
                    transition={{ duration: 2, delay: 0.5, ease: "easeInOut" }}
                  />
                  <motion.path
                    d="M100,550 L500,550 L500,500 L800,500 L800,550 L900,550"
                    className="stroke-gray-600/80 stroke-[2.5px]"
                    fill="none"
                    initial={{ pathLength: 0, opacity: 0 }}
                    animate={{ pathLength: 1, opacity: 1 }}
                    transition={{ duration: 2, delay: 1, ease: "easeInOut" }}
                  />

                  {/* Additional diagonal and vertical connections */}
                  <motion.path
                    d="M300,50 L500,300 L700,550"
                    className="stroke-gray-600/80 stroke-[2.5px]"
                    fill="none"
                    initial={{ pathLength: 0, opacity: 0 }}
                    animate={{ pathLength: 1, opacity: 1 }}
                    transition={{ duration: 1.5, delay: 1.5, ease: "easeInOut" }}
                  />
                  <motion.path
                    d="M200,50 L200,550"
                    className="stroke-gray-600/80 stroke-[2.5px]"
                    fill="none"
                    initial={{ pathLength: 0, opacity: 0 }}
                    animate={{ pathLength: 1, opacity: 1 }}
                    transition={{ duration: 1.5, delay: 2, ease: "easeInOut" }}
                  />
                  <motion.path
                    d="M600,50 L600,550"
                    className="stroke-gray-600/80 stroke-[2.5px]"
                    fill="none"
                    initial={{ pathLength: 0, opacity: 0 }}
                    animate={{ pathLength: 1, opacity: 1 }}
                    transition={{ duration: 1.5, delay: 2, ease: "easeInOut" }}
                  />
                  <motion.path
                    d="M800,50 L800,550"
                    className="stroke-gray-600/80 stroke-[2.5px]"
                    fill="none"
                    initial={{ pathLength: 0, opacity: 0 }}
                    animate={{ pathLength: 1, opacity: 1 }}
                    transition={{ duration: 1.5, delay: 2, ease: "easeInOut" }}
                  />
                  <motion.path
                    d="M400,50 L600,300 L400,550"
                    className="stroke-gray-600/80 stroke-[2.5px]"
                    fill="none"
                    initial={{ pathLength: 0, opacity: 0 }}
                    animate={{ pathLength: 1, opacity: 1 }}
                    transition={{ duration: 1.5, delay: 2.5, ease: "easeInOut" }}
                  />

                  {/* Connection nodes with pulse animation */}
                  {[
                    { cx: 200, cy: 50 }, { cx: 200, cy: 300 }, { cx: 200, cy: 550 },
                    { cx: 400, cy: 50 }, { cx: 400, cy: 150 }, { cx: 400, cy: 550 },
                    { cx: 600, cy: 50 }, { cx: 600, cy: 300 }, { cx: 600, cy: 550 },
                    { cx: 800, cy: 50 }, { cx: 800, cy: 300 }, { cx: 800, cy: 550 },
                    { cx: 500, cy: 300 }, { cx: 300, cy: 350 }, { cx: 700, cy: 150 }
                  ].map((node, index) => (
                    <motion.circle
                      key={index}
                      cx={node.cx}
                      cy={node.cy}
                      r="4"
                      className="fill-purple-400/80"
                      initial={{ scale: 0 }}
                      animate={{ 
                        scale: [1, 1.5, 1],
                        opacity: [0.8, 1, 0.8]
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        delay: index * 0.2,
                        ease: "easeInOut"
                      }}
                    />
                  ))}

                  {/* Animated pulse effect */}
                  <motion.circle
                    r="5"
                    className="fill-purple-400/60"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: [0, 1, 0] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <animateMotion
                      dur="6s"
                      repeatCount="indefinite"
                      path="M100,50 L400,50 L400,150 L700,150 L700,50 L900,50"
                    />
                  </motion.circle>
                  <motion.circle
                    r="5"
                    className="fill-purple-400/60"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: [0, 1, 0] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                  >
                    <animateMotion
                      dur="8s"
                      repeatCount="indefinite"
                      path="M300,50 L500,300 L700,550"
                    />
                  </motion.circle>
                </svg>
              </div>

              {/* Right column - Framework Details */}
              <div className="w-full lg:w-1/2 h-[400px] flex items-center">
                <div className="w-full">
                  {/* Creators Section */}
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5 }}
                    className="bg-[#1a1a2e]/30 backdrop-blur-sm border border-gray-800/50 p-8 rounded-xl hover:border-purple-500/30 transition-all duration-300 mb-8"
                  >
                    <h3 className="text-xl font-inter font-semibold bg-gradient-to-r from-purple-300 to-pink-300 bg-clip-text text-transparent mb-4">
                      Creators
                    </h3>
                    <div className="flex flex-col space-y-6">
                      <a href="https://www.linkedin.com/in/profponytail/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 group">
                        <svg className="w-6 h-6 text-blue-400 group-hover:text-blue-300 transition-colors" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                        </svg>
                        <span className="text-gray-300 group-hover:text-white transition-colors">Dr. Vipul Mathur, IIM Calcutta Professor (former)</span>
                      </a>
                      <a href="https://www.linkedin.com/in/sohumsen/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 group">
                        <svg className="w-6 h-6 text-blue-400 group-hover:text-blue-300 transition-colors" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                        </svg>
                        <span className="text-gray-300 group-hover:text-white transition-colors">Sohum Sen, IIM Calcutta MBA</span>
                      </a>
                    </div>
                  </motion.div>

                  {/* Epistemological Foundation */}
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    className="bg-[#1a1a2e]/30 backdrop-blur-sm border border-gray-800/50 p-8 rounded-xl hover:border-purple-500/30 transition-all duration-300"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 flex items-center justify-center">
                        <svg className="w-12 h-12 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-lg font-inter font-semibold bg-gradient-to-r from-purple-300 to-pink-300 bg-clip-text text-transparent mb-2">
                          Epistemological Foundation
                        </h3>
                        <p className="text-gray-400 text-sm leading-relaxed">
                          Built on first-principles research and experiential data from real IIM interviews, ensuring high-fidelity simulations. Our framework integrates bleeding-edge AI research with best practices refined through extensive interview experience.                        </p>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Interview Models Section */}
        <motion.section
          style={{ y: modelsY }}
          className="py-16 px-4 relative w-full"
        >
          <div className="absolute inset-0 bg-gradient-to-b from-gray-900/80 via-gray-900/90 to-gray-900/80 backdrop-blur-lg" />
          <div className="max-w-[1200px] mx-auto text-center mb-8 sm:mb-16 relative z-10">
            <motion.h2 
              className="text-3xl sm:text-4xl md:text-5xl font-inter font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent mb-4 sm:mb-6 px-4"
              animate={{
                textShadow: interviewModelsTitle.isComplete ? [
                  "0 0 20px rgba(168, 85, 247, 0)",
                  "0 0 40px rgba(168, 85, 247, 0.2)",
                  "0 0 20px rgba(168, 85, 247, 0)",
                ] : "none",
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              {interviewModelsTitle.displayText}
            </motion.h2>
            <p className="text-lg sm:text-xl text-gray-200 max-w-3xl mx-auto px-4">
            Interviews come in all flavors, but if you strip them down to their core, they fall on a spectrum—Chill or Grill. One builds you up, the other breaks you down. Both are essential. One polishes, the other stress-tests. You don't sharpen a blade on silk. You need both. Some days, you refine. Some days, you survive. 
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 w-full relative z-10">
            {/* Standard Configuration */}
            <motion.div 
              whileHover={{ y: -5, scale: 1.02 }}
              className="bg-gradient-to-br from-purple-500/5 via-pink-500/5 to-purple-400/5 backdrop-blur-xl border border-gray-800/50 rounded-2xl p-6 sm:p-8 relative group hover:border-purple-500/30 transition-all duration-300 mx-4"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-gray-900/60 to-gray-900/30 rounded-2xl" />
              <div className="relative z-10">
                <motion.div 
                  className="text-2xl sm:text-3xl font-inter font-semibold bg-gradient-to-r from-purple-300 to-pink-300 bg-clip-text text-transparent mb-4 text-center"
                  whileHover={{ scale: 1.02 }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                >
                  "Chill" Interview
                </motion.div>
                <div className="text-gray-300 text-base sm:text-lg mb-6 font-light text-center">
                Think of this as a seasoned mentor, not a ruthless interrogator. You'll be challenged, but the goal is to refine—not rattle—you. 
                </div>
                <div className="space-y-4 text-left">
                  <div className="bg-gray-900/30 rounded-xl p-4 backdrop-blur-sm border border-gray-800/50 hover:border-purple-500/20 transition-all duration-300">
                    <div className="text-purple-300 mb-2 text-lg">A Gentle Breeze</div>
                    <div className="text-gray-400 leading-relaxed">Think of it as a firm handshake and a smooth drink. Best for those refining their articulation, looking for constructive feedback. The goal is to guide you through your strengths and weaknesses. Like a mentor who nudges you in the right direction.</div>
                  </div>
                  <div className="bg-gray-900/30 rounded-xl p-4 backdrop-blur-sm border border-gray-800/50 hover:border-purple-500/20 transition-all duration-300">
                    <div className="text-purple-300 mb-2 text-lg">Your takeaway?</div>
                    <div className="text-gray-400 leading-relaxed">You walk out informed, with a clearer understanding of what worked and what didn't. The feedback will be encouraging. Even if you slip, you get pointers to recalibrate.</div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Dynamic Configuration */}
            <motion.div 
              whileHover={{ y: -5, scale: 1.02 }}
              className="bg-gradient-to-br from-purple-500/5 via-pink-500/5 to-purple-400/5 backdrop-blur-xl border border-gray-800/50 rounded-2xl p-6 sm:p-8 relative group hover:border-purple-500/30 transition-all duration-300 mx-4"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-gray-900/60 to-gray-900/30 rounded-2xl" />
              <div className="relative z-10">
                <motion.div 
                  className="text-2xl sm:text-3xl font-inter font-semibold bg-gradient-to-r from-purple-300 to-pink-300 bg-clip-text text-transparent mb-4 text-center"
                  whileHover={{ scale: 1.02 }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                >
                  "Grill" Interview
                </motion.div>
                <div className="text-gray-300 text-base sm:text-lg mb-6 font-light text-center">
                No hand-holding here. This is pressure, scrutiny, and skepticism rolled into one. If your answers have weak spots, we'll find them. 
                </div>
                <div className="space-y-4 text-left">
                  <div className="bg-gray-900/30 rounded-xl p-4 backdrop-blur-sm border border-gray-800/50 hover:border-purple-500/20 transition-all duration-300">
                    <div className="text-purple-300 mb-2 text-lg">Trial by Fire</div>
                    <div className="text-gray-400 leading-relaxed">Direct, intense, and relentless. No small talk, just fire. Like facing the wolfpack. Best for those testing their mental agility, quick thinking, and resilience. The goal is to break you down and see what's left standing. Can you defend every word on your resume?</div>
                  </div>
                  <div className="bg-gray-900/30 rounded-xl p-4 backdrop-blur-sm border border-gray-800/50 hover:border-purple-500/20 transition-all duration-300">
                    <div className="text-purple-300 mb-2 text-lg">Your takeaway?</div>
                    <div className="text-gray-400 leading-relaxed">You walk out battle-tested, knowing where you cracked and where you didn't. The feedback will be brutally honest - and may hit like daggers. If you fumble, we let you sit in the silence.</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.section>

        {/* The Problem We Solve Section */}
        <motion.section
          style={{ y: problemsY }}
          className="py-12 sm:py-16 lg:py-20 relative w-full"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 via-pink-500/10 to-purple-400/10 backdrop-blur-xl" />
          <div className="relative z-10 w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Fixed Heading */}
            <motion.div 
              className="text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-purple-300 to-pink-300 bg-clip-text text-transparent mb-4">
                The Problems We Solve
              </h2>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                Our AI-powered platform addresses key challenges in MBA interview preparation
              </p>
            </motion.div>

            {/* Ticker Container */}
            <div className="ticker-container">
              <div className="animate-ticker">
                {/* Double the cards for seamless loop */}
                {[...problemSolutions, ...problemSolutions].map((item, index) => (
                  <motion.div
                    key={index}
                    className="min-w-[300px] bg-[#1a1a2e]/30 backdrop-blur-sm border border-gray-800/50 p-6 rounded-xl hover:border-purple-500/30 transition-all duration-300"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-gray-800/50 flex items-center justify-center">
                        {index % 10 === 0 && (
                          <svg className="w-5 h-5 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        )}
                        {index % 10 === 1 && (
                          <svg className="w-5 h-5 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12a9 9 0 11-18 0 9 9 0 0118 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                          </svg>
                        )}
                        {index % 10 === 2 && (
                          <svg className="w-5 h-5 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                          </svg>
                        )}
                        {index % 10 === 3 && (
                          <svg className="w-5 h-5 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                        )}
                        {index % 10 === 4 && (
                          <svg className="w-5 h-5 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                          </svg>
                        )}
                        {index % 10 === 5 && (
                          <svg className="w-5 h-5 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                          </svg>
                        )}
                        {index % 10 === 6 && (
                          <svg className="w-5 h-5 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                          </svg>
                        )}
                        {index % 10 === 7 && (
                          <svg className="w-5 h-5 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                        )}
                        {index % 10 === 8 && (
                          <svg className="w-5 h-5 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                          </svg>
                        )}
                        {index % 10 === 9 && (
                          <svg className="w-5 h-5 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        )}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-inter font-semibold bg-gradient-to-r from-purple-300 to-pink-300 bg-clip-text text-transparent mb-2">
                          {item.problem}
                        </h3>
                        <p className="text-gray-400 text-sm leading-relaxed">
                          {item.solution}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </motion.section>

        {/* FAQ Section */}
        <motion.section
          style={{ y: faqY }}
          className="py-16 px-4 relative w-full"
        >
          <div className="max-w-[1200px] mx-auto w-full">
            <motion.h2 
              className="text-3xl sm:text-4xl md:text-5xl text-center font-inter font-bold text-white mb-3 sm:mb-4"
            >
              {faqTitle.displayText}
            </motion.h2>
            <motion.p className="text-lg sm:text-xl text-center text-gray-400 mb-12 sm:mb-16">
              Your questions, answered
            </motion.p>

            <div className="space-y-4 w-full max-w-3xl mx-auto">
              {[
                {
                  question: "AI interviews can never be the same as humans. Why bother?",
                  answer: "Look, human interviews are a mixed bag. They are expensive. They're biased. They have blind spots. And let's be honest—not all human interviewers are even good at their job. Maybe the top 2% of them are. The tech is already replacing mid-level human programmers. We believe it can actually play at the 98th percentile of human interviewers. That's the bar we're setting. So with us, you get consistent (top) human quality without the drawbacks."
                },
                {
                  question: "Why should I trust you?",
                  answer: "You don't have to. This is an engine—one that generates questions designed to make you sweat every detail of your resume. The goal? To expose you to every curveball, every follow-up that might come your way. And then? Immediate feedback. That's the cycle. That's how you get better."
                },
                {
                  question: "How is this different from me running my resume through ChatGPT or Gemini?",
                  answer: "Quality. That's the difference. This isn't just about spitting out generic questions. We've designed our product based on how Indian B-School interviewers actually think. We've been panelists at one of the top three IIMs. We built that insight into the product. Try it and see for yourself."
                },
                {
                  question: "What are the prerequisites for attempting these interviews?",
                  answer: "A resume—PDF format, preferably. A quiet corner. 30 to 40 minutes. That's it."
                },
                {
                  question: "What is a chill and grill interview?",
                  answer: "Interviews come in many shapes and sizes. But if you were to map them, you'd find two extremes: one is supportive, the other is combative. Both are challenging. Both are thorough. Think of movies—on one end, you have intense thrillers; on the other, feel-good dramas. On a given day, the interview you take will be a mix of both, and we let you prepare at both ends of that spectrum."
                },
                {
                  question: "How many interviews should I attempt?",
                  answer: "As many as you want. No two interviews will be identical. That said, four is a solid starting point. Then, if you need more, top up in sets of four. Split them between chill and grill as you see fit."
                },
                {
                  question: "What's the real-time feedback mechanism? And is the score accurate?",
                  answer: "Our feedback system is based on 700+ IIM interviews — the real deal. We've embedded how panelists think and evaluate candidates. The score is an indicator, not gospel. It quantifies feedback, but it's not a crystal ball. It's a tool, use it wisely."
                },
                {
                  question: "Will this actually improve my chances?",
                  answer: "Let's put it this way: Most candidates walk into an interview untested. You won't be one of them. We push you through the gauntlet first—so when the real thing happens, you've already seen it all."
                },
                {
                  question: "What if I bomb an interview?",
                  answer: "Good. Better to bomb here than in the real thing. The entire point is to find your weak spots in a no-risk environment. No judgment. Just cold, hard feedback."
                },                
                {
                  question: "How many mock interviews should I take?",
                  answer: "We recommend starting with four. If you need more, you can top up in sets of four. Read our Pricing Philosophy for more details."
                },
                {
                  question: "Should I use a mobile or laptop?",
                  answer: "We recommend attempting the mock interview sincerely, which is why we don't want you to be disturbed by phone calls or social media notifications. We suggest using a desktop or laptop with a Chrome browser."
                },                      
                {
                  question: "Can I pause and resume an interview?",
                  answer: "No. Real interviews don't come with a pause button. Once you start, you commit. It's 30-40 minutes, uninterrupted. If that feels too long, well… interviews are longer."
                },      
                {
                  question: "Will my data be stored?",
                  answer: "Only for as long as necessary to refine your sessions. We don't sell or share it. If you want us to forget everything, we'll wipe it clean on request. No strings attached. Read our Privacy Policy for more details."
                },
                {
                  question: "When can I get started?",
                  answer: "Now is as good a time as any."
                }
              ].map((faq, index) => (
                <motion.div 
                  key={index}
                  className="border-b border-gray-800 pb-4 sm:pb-6 group cursor-pointer"
                  onClick={() => {
                    setOpenFaqIndices(prev => 
                      prev.includes(index)
                        ? prev.filter(i => i !== index)
                        : [...prev, index]
                    );
                  }}
                >
                  <div className="flex justify-between items-center">
                    <h3 className="text-base sm:text-lg text-gray-300 group-hover:text-white transition-colors pr-8">{faq.question}</h3>
                    <motion.div 
                      className="text-gray-500 group-hover:text-white transition-colors"
                      animate={{ rotate: openFaqIndices.includes(index) ? 90 : 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      →
                    </motion.div>
                  </div>
                  <motion.div
                    initial={false}
                    animate={{ 
                      height: openFaqIndices.includes(index) ? "auto" : 0,
                      opacity: openFaqIndices.includes(index) ? 1 : 0
                    }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    className="overflow-hidden"
                  >
                    <p className="text-gray-400 mt-4 leading-relaxed pr-8 text-sm sm:text-base">
                      {faq.answer}
                    </p>
                  </motion.div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.section>

        {/* Enhanced CTA Section with motion values */}
        <motion.section
          style={{ y: ctaY }}
          className="min-h-screen flex items-center py-16 sm:py-24 lg:py-40 px-4"
        >
          <div className="max-w-4xl mx-auto text-center w-full">
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="relative group bg-[#1a1a2e]/20 p-6 sm:p-12 lg:p-20 rounded-3xl border border-gray-800/50 overflow-hidden"
            >
              <motion.div 
                className="absolute inset-0 bg-gradient-to-r from-purple-500/10 via-pink-500/10 to-purple-400/10"
                animate={{
                  opacity: [0, 0.1, 0],
                  scale: [1, 1.2, 1],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
              <div className="relative z-10">
                <motion.h2 
                  className="text-3xl sm:text-4xl md:text-5xl font-inter font-bold mb-6 sm:mb-8 bg-gradient-to-r from-purple-400 via-pink-400 to-purple-300 bg-clip-text text-transparent"
                  animate={{
                    backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
                  }}
                  transition={{
                    duration: 8,
                    repeat: Infinity,
                    ease: "linear"
                  }}
                  style={{ backgroundSize: "200% 200%" }}
                >
                  {ctaTitle.displayText}
                </motion.h2>
                <p className="text-base sm:text-lg text-gray-400 mb-8 sm:mb-12 max-w-2xl mx-auto leading-relaxed">
                  Join thousands of successful candidates who have mastered their interview skills with our platform
                </p>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="group relative px-6 sm:px-12 py-4 sm:py-6 bg-gradient-to-r from-purple-500 via-pink-500 to-purple-400 rounded-2xl text-lg sm:text-xl font-[Cabinet Grotesk] font-semibold transition-all duration-300 w-full sm:w-auto"
                  onClick={handleGetStarted}
                >
                  <span className="absolute inset-0 bg-white mix-blend-overlay opacity-0 group-hover:opacity-10 transition-opacity duration-300 rounded-2xl" />
                  <motion.span className="relative z-10 text-white">
                    Get Started
                  </motion.span>
                </motion.button>
              </div>
            </motion.div>
          </div>
        </motion.section>
      </div>
    </div>
  );
};

// Add styles to document
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = `
@keyframes ticker {
  0% {
    transform: translate3d(-100%, 0, 0);
  }
  100% {
    transform: translate3d(0%, 0, 0);
  }
}

.animate-ticker {
  animation: ticker 30s linear infinite;
  will-change: transform;
  display: flex;
  gap: 2rem;
}

.animate-ticker:hover {
  animation-play-state: paused;
}

.problem-solutions-container {
  position: relative;
  width: 100%;
  overflow: hidden;
}

.fixed-title {
  position: relative;
  text-align: center;
  z-index: 10;
  margin-bottom: 2rem;
}

.ticker-container {
  position: relative;
  width: 100%;
  overflow: hidden;
}
`;
  document.head.appendChild(styleSheet);
}

export default LandingPage; 